import { useDebouncedFunction } from '@instinet-analytics/core-lib';
import { useCallback } from 'react';
import { useReferredState } from '@instinet-analytics/core-lib';

export function useWidgetConfigUpdater<TConfig>(
    dispatchConfigChanged: (nextDelta: Partial<TConfig>) => void,
    onChange?: (nextDelta: Partial<TConfig>) => void
) {
    const [, deltaRef, setDelta] = useReferredState<Partial<TConfig>>({});

    const onChangeDebouncedDelta = useCallback(
        (nextDelta: Partial<TConfig>) => {
            const cumulativeDelta = { ...deltaRef.current, ...nextDelta };
            setDelta(cumulativeDelta);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const [debouncedConfigurationChange] = useDebouncedFunction(
        () => dispatchConfigChanged(deltaRef.current),
        2000,
        {
            callOnUnmount: true,
        }
    );

    const applyChange = (debounce: boolean, delta: Partial<TConfig>) => {
        if (onChange) {
            onChange(delta);
        } else if (debounce) {
            onChangeDebouncedDelta(delta);
            debouncedConfigurationChange();
        } else {
            dispatchConfigChanged(delta);
        }
    };

    const debouncedApplyWidgetConfigChange = useCallback(
        (delta: Partial<TConfig>) => {
            applyChange(true, delta);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const immediateApplyWidgetConfigChange = useCallback(
        (delta: Partial<TConfig>) => {
            applyChange(false, delta);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    return {
        debouncedApplyWidgetConfigChange,
        immediateApplyWidgetConfigChange,
    };
}
